import { useState } from 'react'

import {
  useFetchResponse,
  useFetchResponseWithUtils,
  useReselector,
} from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  FETCH_CHECKOUT_SESSION_KEY,
  fetchEligibleStripeProducts,
  fetchRecommendedOffers,
  type StripeProduct,
} from '@/actions/settings/billingActions'
import { updateFinancialProfile } from '@/actions/financialProfileActions'
import { getFetchError, invalidateFetch } from '@/reducers/fetch'
import { logHeardError } from '@/utils/errorHelpers'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { SIGNUP_PATHS } from '@/features/user/signup/helpers'
import { useSignupCheckout } from '@/features/user/signup/checkout/useSignupCheckout'
import { PLAN_UNAVAILABLE_MESSAGE } from '@/features/user/signup/payment/catalogHelpers'
import { CHECKOUT_ERROR_MESSAGE } from '@/features/user/signup/checkout/checkoutErrorMessage'
import type { CatchUpBkTreatment } from '@/features/user/signup/payment/helpers'
import type { CheckoutSelection } from '@/features/user/signup/plans/signupPlans.types'
import { useNewCatalogSignup } from '@/features/user/signup/plans/useNewCatalogSignup'

import { COMPLIANCE_PATHS } from '../lib/constants'
import { selectAnswers, selectRecommendedOffers } from '../compliance.selectors'
import { earlyPracticeTierHint } from './earlyPracticeHint'
import { writePlanSelection } from './planSelection'
import {
  annualSavingLabelParts,
  annualSavingPercent,
} from './recommendedPlan.copy'
import {
  BILLING_INTERVALS,
  priceForInterval,
  type BillingInterval,
  type RankedTierOffer,
  type RecommendationSignalName,
} from './recommendation.types'

/** Matched on the code, not the message, so it survives copy changes. */
const MISSING_HOME_STATE_ERROR_CODE = 'missing_home_state'
const SCREEN_NAME = COMPLIANCE_PATHS.planPricing

/** Module-level so its identity is stable across renders. */
const skipEligibleProducts = () => () => Promise.resolve(undefined)

/**
 * Which of the screen's mutually exclusive outcomes is in force. `ready` is the
 * only one carrying an offer, which is what makes "is there something to buy"
 * a single check rather than something callers infer from a loading flag and a
 * total.
 */
export type PlanCheckoutState =
  | { status: 'loading' }
  /** Recommendations off, or the fetch failed. */
  | { status: 'unavailable' }
  | { status: 'needsHomeState' }
  | { status: 'insufficientSignals'; missing: RecommendationSignalName[] }
  | { status: 'noOffers' }
  /** Offers exist, but the one being previewed carries no chargeable price for
   *  the interval on screen. The cards still render — the customer can pick a
   *  different tier, and picking this one refuses with a reason — but there is
   *  nothing to quote in the summary. */
  | {
      status: 'unpriced'
      offers: RankedTierOffer[]
      previewOffer: RankedTierOffer
    }
  | {
      status: 'ready'
      offers: RankedTierOffer[]
      previewOffer: RankedTierOffer
      /** Guaranteed chargeable: `ready` is not reachable without one, so callers
       *  never have to ask whether the plan they are quoting has a price. */
      previewPriceCents: number
    }

export interface PlanCheckout {
  state: PlanCheckoutState
  /** The interval actually in force: the one asked for, unless no offer prices
   *  annually, in which case monthly. Derived rather than corrected after the
   *  fact, so there is no render where the quote and the toggle disagree. */
  interval: BillingInterval
  hasAnnualOffer: boolean
  /** e.g. "Save 15%", for the Annual toggle's badge. */
  savingHighlight: string | null
  /** The previewed tier's price for `interval`, or null when nothing is
   *  quotable — a tier with no chargeable amount included. */
  previewPriceCents: number | null
  previewName: string | null
  freeTrialEnabled: boolean
  catchUpBk: CatchUpBkTreatment
  /** A checkout is in flight. Every entry point into checkout reads this, so a
   *  second one cannot open a duplicate Stripe session. */
  busy: boolean
  errorMessage: string | null
  selectOffer: (offer: RankedTierOffer) => void
  /** Checks out whatever `previewOffer` currently is. */
  payPreview: () => void
  refetch: () => void
  homeState: {
    value: string
    set: (value: string) => void
    error: string | undefined
    saving: boolean
    saveAndRetry: () => Promise<void>
  }
}

/**
 * Owns the plan offers, the tier the customer has chosen, and the Stripe
 * checkout for it — everything the pricing page needs to quote a plan and bill
 * it. Held here rather than inside the plan section so the page can read it
 * directly: the summary beside the cards quotes the same offer the cards show,
 * and Pay Now bills that offer, without either being reported up through
 * effects.
 */
export const usePlanCheckout = (
  requestedInterval: BillingInterval,
  /** Whether the server has waived the catch-up fee. Only the API can decide it,
   *  so it is passed in rather than derived here, and it has to reach
   *  `useSignupCheckout` because that is what leaves the fee out of the session. */
  serverWaived = false
): PlanCheckout => {
  const dispatch = useAppDispatch()
  const [selectedTier, setSelectedTier] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [selectionError, setSelectionError] = useState<string>()
  const [lastSelection, setLastSelection] = useState<CheckoutSelection>()
  const [homeStateValue, setHomeStateValue] = useState('')
  const [savingHomeState, setSavingHomeState] = useState(false)
  const [homeStateError, setHomeStateError] = useState<string>()

  const financialProfile = useReselector(getFinancialProfile)
  const checkoutError = useReselector(getFetchError, FETCH_CHECKOUT_SESSION_KEY)
  // The intake is hydrated by QualifiedOnlyRoute, which gates this route, so the
  // answers are here on a direct load as well as on arrival from the report.
  const answers = useReselector(selectAnswers)
  const tierHint = earlyPracticeTierHint(answers)
  const {
    result: fetched,
    refetch,
    loading,
  } = useFetchResponseWithUtils(fetchRecommendedOffers, {
    context: 'report',
    recommendedTier: tierHint,
  })
  // Seeded from what the report already loaded, so arriving from it renders the
  // offers immediately instead of holding a spinner over a second request for
  // the same answer.
  //
  // Only when this page asks for what the report asked for. The report fetches
  // without a tier hint, and the hint moves `isRecommended`, which is what
  // `previewOffer` quotes from: seeding a hinted page renders one plan's price
  // and re-quotes to another when the fetch lands.
  const storedOffers = useReselector(selectRecommendedOffers)
  const result = fetched ?? (tierHint ? undefined : (storedOffers ?? undefined))
  const catalogFlag = useNewCatalogSignup()
  const catalog = catalogFlag === true
  const products = useFetchResponse(
    catalogFlag === false ? fetchEligibleStripeProducts : skipEligibleProducts
  )
  const {
    submitWith,
    loading: checkoutLoading,
    freeTrialEnabled,
    catchUpBkTreatment: catchUpBk,
  } = useSignupCheckout({
    successUrl: SIGNUP_PATHS.success,
    screenName: SCREEN_NAME,
    serverWaived,
  })

  const offers = result?.status === 'ok' ? result.offers : []
  // Whether the toggle has anywhere to go. `.some()`, because one annual price
  // in the set is enough to be worth offering.
  const hasAnnualOffer = offers.some((offer) =>
    priceForInterval(offer, BILLING_INTERVALS.year)
  )

  // Falls back to the recommendation until the customer picks, so there is
  // always a plan to quote.
  const previewOffer =
    offers.find((offer) => offer.tier === selectedTier) ??
    offers.find((offer) => offer.isRecommended) ??
    offers[0]

  // Keyed to the offer being quoted rather than to the set: a mixed catalog can
  // price one tier annually and not another, and asking for a price the chosen
  // tier does not have leaves nothing to quote at all.
  const canQuoteAnnual = previewOffer
    ? Boolean(priceForInterval(previewOffer, BILLING_INTERVALS.year))
    : hasAnnualOffer
  const interval =
    requestedInterval === BILLING_INTERVALS.year && canQuoteAnnual
      ? BILLING_INTERVALS.year
      : BILLING_INTERVALS.month

  const previewPrice = previewOffer
    ? priceForInterval(previewOffer, interval)
    : undefined
  const previewPriceCents =
    typeof previewPrice?.unitAmountInCents === 'number'
      ? previewPrice.unitAmountInCents
      : null

  const productForPrice = (priceId: string): StripeProduct | undefined =>
    products?.find((product) => product.default_price.id === priceId)

  /** Off: today's rule, the price must still match an eligible Stripe product. */
  const selectionFor = (price: {
    defaultPriceId: string
    productId: string
  }): CheckoutSelection | undefined => {
    if (catalog) {
      return { priceId: price.defaultPriceId, productId: price.productId }
    }
    const product = productForPrice(price.defaultPriceId)
    return product
      ? { priceId: product.default_price.id, productId: product.id }
      : undefined
  }

  const startCheckout = async (selection: CheckoutSelection) => {
    setSubmitting(true)
    let redirected = false
    try {
      redirected = await submitWith(selection)
    } finally {
      if (!redirected) setSubmitting(false)
    }
  }

  const chooseTier = async (offer: RankedTierOffer) => {
    const price = priceForInterval(offer, interval)
    const chargeable = typeof price?.unitAmountInCents === 'number'
    const selection = price ? selectionFor(price) : undefined
    if (!price || !chargeable || !selection) {
      setSelectionError(PLAN_UNAVAILABLE_MESSAGE)
      logHeardError('compliance plans: chosen tier has no chargeable price', {
        context: 'compliance: choose tier',
        tier: offer.tier,
        interval,
        hasPrice: Boolean(price),
        hasAmount: typeof price?.unitAmountInCents === 'number',
        hasSelection: Boolean(selection),
      })
      return
    }
    setSelectionError(undefined)
    writePlanSelection({ tier: offer.tier, priceId: price.defaultPriceId })
    setLastSelection(selection)
    await startCheckout(selection)
  }

  const saveHomeStateAndRetry = async () => {
    setSavingHomeState(true)
    setHomeStateError(undefined)
    const saved = await updateFinancialProfile(financialProfile?.id, {
      homeState: homeStateValue,
    })(dispatch)
    setSavingHomeState(false)
    if (!saved) {
      setHomeStateError('We could not save your state. Please try again.')
      return
    }
    dispatch(invalidateFetch(FETCH_CHECKOUT_SESSION_KEY))
    if (lastSelection) await startCheckout(lastSelection)
  }

  const busy =
    submitting ||
    checkoutLoading ||
    catalogFlag === null ||
    (catalogFlag === false && products === undefined)

  const state = ((): PlanCheckoutState => {
    // Only a load with nothing to show is a spinner. Seeded offers render while
    // the refetch is still in flight, which is the whole point of seeding them.
    if (loading && !result) return { status: 'loading' }
    if (!result || result.status === 'feature_disabled')
      return { status: 'unavailable' }
    if (checkoutError?.errorCode === MISSING_HOME_STATE_ERROR_CODE)
      return { status: 'needsHomeState' }
    if (result.status === 'insufficient_signals')
      return { status: 'insufficientSignals', missing: result.missing }
    if (!previewOffer) return { status: 'noOffers' }
    if (previewPriceCents === null)
      return { status: 'unpriced', offers, previewOffer }
    return { status: 'ready', offers, previewOffer, previewPriceCents }
  })()

  return {
    state,
    interval,
    hasAnnualOffer,
    savingHighlight:
      annualSavingLabelParts(
        offers.map((offer) =>
          annualSavingPercent(
            priceForInterval(offer, BILLING_INTERVALS.month),
            priceForInterval(offer, BILLING_INTERVALS.year)
          )
        )
      )?.highlight ?? null,
    previewPriceCents,
    previewName: previewOffer?.displayName ?? null,
    freeTrialEnabled,
    catchUpBk,
    busy,
    errorMessage:
      selectionError ?? (checkoutError ? CHECKOUT_ERROR_MESSAGE : null),
    selectOffer: (offer) => setSelectedTier(offer.tier),
    payPreview: () => {
      // Checked here and not only on the button: this is the guard that stops a
      // duplicate Stripe session, so it belongs with the action.
      if (busy || !previewOffer) return
      void chooseTier(previewOffer)
    },
    refetch,
    homeState: {
      value: homeStateValue,
      set: setHomeStateValue,
      error: homeStateError,
      saving: savingHomeState,
      saveAndRetry: saveHomeStateAndRetry,
    },
  }
}
